import { useCallback } from 'react';
import type { ComponentProps, FC } from 'react';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { mergeProps } from '@react-aria/utils';

import type { ApiMentionJSON } from '@/mastodon/api_types/statuses';
import { getCollectionPath } from '@/mastodon/features/collections/utils';
import { isRedesignEnabled } from '@/mastodon/utils/environment';
import type { OnElementHandler } from '@/mastodon/utils/html';

import { HashtagMenu } from '../hashtag_menu';
import { MenuTriggerText } from '../menu';
import { Tooltip } from '../tooltip';

import classes from './handled_link.module.scss';

export interface HandledLinkProps {
  href: string;
  text: string;
  prevText?: string;
  hashtagAccountId?: string;
  mention?: Pick<ApiMentionJSON, 'id' | 'acct'>;
  collectionId?: string;
}

export const HandledLink: FC<HandledLinkProps & ComponentProps<'a'>> = ({
  href,
  text,
  prevText,
  hashtagAccountId,
  mention,
  collectionId,
  className,
  children,
  ...props
}) => {
  // Handle hashtags
  if (
    (text.startsWith('#') ||
      prevText?.endsWith('#') ||
      text.startsWith('＃') ||
      prevText?.endsWith('＃')) &&
    !text.includes('%')
  ) {
    const hashtag = text.slice(1).trim();

    if (isRedesignEnabled()) {
      return (
        <HashtagMenu tagId={hashtag} accountId={hashtagAccountId}>
          <MenuTriggerText
            className={classes.hashtag}
            href={`/tags/${encodeURIComponent(hashtag)}`}
          >
            {children}
          </MenuTriggerText>
        </HashtagMenu>
      );
    }

    return (
      <Link
        className={classNames('mention hashtag', className)}
        to={`/tags/${encodeURIComponent(hashtag)}`}
        rel='tag'
        data-menu-hashtag={hashtagAccountId}
      >
        {children}
      </Link>
    );
  } else if (mention) {
    // Handle mentions
    return (
      <Link
        className={classNames('mention', className)}
        to={{ pathname: `/@${mention.acct}`, state: { reference: 'status' } }}
        title={`@${mention.acct}`}
        data-hover-card-account={mention.id}
        data-hover-card-reference='status'
      >
        {children}
      </Link>
    );
  } else if (collectionId) {
    return (
      <Link
        className={classNames(className)}
        to={getCollectionPath(collectionId)}
      >
        {children}
      </Link>
    );
  }

  // Non-absolute paths treated as internal links. This shouldn't happen, but just in case.
  if (href.startsWith('/')) {
    return (
      <Tooltip text={href}>
        {({ getTooltipProps }) => (
          <Link
            {...getTooltipProps()}
            className={classNames('unhandled-link', className)}
            to={href}
          >
            {children}
          </Link>
        )}
      </Tooltip>
    );
  }

  return (
    <Tooltip text={href}>
      {({ getTooltipProps }) => (
        <a
          {...mergeProps(props, getTooltipProps())}
          href={href}
          className={classNames('unhandled-link', className)}
          target='_blank'
          rel='noopener'
          translate='no'
        >
          {children}
        </a>
      )}
    </Tooltip>
  );
};

export const useElementHandledLink = ({
  hashtagAccountId,
  hrefToCollectionId: hrefToCollection,
  hrefToMention,
}: {
  hashtagAccountId?: string;
  hrefToCollectionId?: (href: string) => string | undefined;
  hrefToMention?: (href: string) => ApiMentionJSON | undefined;
} = {}) => {
  const onElement = useCallback<OnElementHandler>(
    (element, { key, ...props }, children) => {
      if (element instanceof HTMLAnchorElement) {
        const mention = hrefToMention?.(element.href);
        const collectionId = hrefToCollection?.(element.href);
        return (
          <HandledLink
            {...props}
            key={key as string} // React requires keys to not be part of spread props.
            href={element.href}
            text={element.innerText}
            prevText={element.previousSibling?.textContent ?? undefined}
            hashtagAccountId={hashtagAccountId}
            mention={mention}
            collectionId={collectionId}
          >
            {children}
          </HandledLink>
        );
      }
      return undefined;
    },
    [hashtagAccountId, hrefToCollection, hrefToMention],
  );
  return { onElement };
};
